import { useEffect, useRef } from "react";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { logLiveKit, LogLevel } from "utils/sentryLogger";

export const AudioTrack = ({ track, isLocal, participantIdentity }) => {
  const { activeAudioSinkId } = useVideoContext();
  const audioEl = useRef();
  const id = useRef();

  useEffect(() => {
    if (isLocal || !track ) return;
    audioEl.current = track.attach();
    id.current = `at-${Math.random().toString(36).slice(-6)}`;
    audioEl.current.id = id.current;
    document.body.appendChild(audioEl.current);
    return () => {
      const audEl = track.detach(audioEl.current);
      audEl.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  // The bot's inbound track carries no packets between phrases, so the browser
  // flags it muted and has to wake it back up at every speech start — on
  // iPadOS 26 the first seconds after that wake-up are lost. Log the track's
  // mute/unmute cycle and the element's playback stalls so a repro shows it.
  useEffect(() => {
    if (isLocal || !track || !participantIdentity?.includes("observer")) return undefined;
    const el = audioEl.current;
    const mediaTrack = track.mediaStreamTrack;
    if (!el || !mediaTrack) return undefined;

    let mutedAt = null;
    const log = (name, data = {}) => {
      logLiveKit(`Okobot audio track ${name}`, {
        event: `okobot_audio_${name}`,
        trackMuted: mediaTrack.muted,
        elementPaused: el.paused,
        elementReadyState: el.readyState,
        ...data,
      });
    };
    const onMute = () => {
      mutedAt = Date.now();
      log("mute");
    };
    const onUnmute = () => log("unmute", {
      silentSeconds: mutedAt ? Math.round((Date.now() - mutedAt) / 1000) : null,
    });
    const onElementEvent = (e) => log(`element_${e.type}`);
    const elementEvents = ["playing", "waiting", "stalled", "pause", "play", "suspend"];

    mediaTrack.addEventListener("mute", onMute);
    mediaTrack.addEventListener("unmute", onUnmute);
    elementEvents.forEach((name) => el.addEventListener(name, onElementEvent));
    return () => {
      mediaTrack.removeEventListener("mute", onMute);
      mediaTrack.removeEventListener("unmute", onUnmute);
      elementEvents.forEach((name) => el.removeEventListener(name, onElementEvent));
    };
  }, [track, isLocal, participantIdentity]);

  // setSinkId is missing on some browsers, and Safari 26 rejects it outside a
  // user-gesture call stack (NotAllowedError "A user gesture is required") —
  // which left these long-lived elements riding the OS default route, where a
  // mid-session Bluetooth route re-evaluation could silently strand them with
  // no event fired and the element still reporting "playing". A rejected pin
  // is therefore retried inside the next capture-phase gesture (the same
  // recovery pattern useAudioPlayback uses for startAudio), and a devicechange
  // re-applies the pin for elements that were never pinned. The failure is
  // logged once per element, not once per tap.
  //
  // "default" is left alone. LiveKit resolves it to the real device id before
  // pinning both its mix context and these elements, so re-applying the raw
  // word here undoes that — and Chrome's echo canceller behaves differently
  // on "default" than on the explicit id it stands for (crbug 40252911).
  useEffect(() => {
    const el = audioEl.current;
    if (!el?.setSinkId || activeAudioSinkId == null || activeAudioSinkId === "default") return undefined;

    let disarmGesture = null;
    let inFlight = false;
    let failureLogged = false;
    let needsFallback = false;
    let cancelled = false;

    const disarm = () => {
      disarmGesture?.();
      disarmGesture = null;
    };

    const armGesture = () => {
      if (disarmGesture) return;
      const onGesture = () => apply(true);
      document.addEventListener("pointerdown", onGesture, true);
      document.addEventListener("keydown", onGesture, true);
      disarmGesture = () => {
        document.removeEventListener("pointerdown", onGesture, true);
        document.removeEventListener("keydown", onGesture, true);
      };
    };

    // sinkId defaults to the chosen device; "" resets to the system default
    // route, used when the pinned device has vanished (NotFoundError) so a
    // disconnected headset can't leave the element stranded on a dead device.
    const apply = (viaGesture, sinkId = activeAudioSinkId) => {
      if (inFlight || cancelled) return;
      inFlight = true;
      el.setSinkId(sinkId)
        .then(() => {
          if (cancelled) return;
          if (viaGesture) {
            logLiveKit("setSinkId recovered on user gesture", {
              event: "audio_sink_change_recovered",
              sinkId,
            });
          }
          disarm();
        })
        .catch((e) => {
          if (cancelled) return;
          if (!failureLogged) {
            failureLogged = true;
            logLiveKit("setSinkId failed on audio element", {
              level: LogLevel.WARNING,
              event: "audio_sink_change_failed",
              sinkId,
              errorName: e?.name,
              errorMessage: e?.message,
            });
          }
          if (e?.name === "NotAllowedError") armGesture();
          else if (e?.name === "NotFoundError" && sinkId !== "") needsFallback = true;
        })
        .finally(() => {
          inFlight = false;
          if (!cancelled && needsFallback) {
            needsFallback = false;
            logLiveKit("setSinkId falling back to default output", {
              event: "audio_sink_fallback_default",
            });
            apply(false, "");
          }
        });
    };

    const onDeviceChange = () => apply(false);
    navigator.mediaDevices?.addEventListener?.("devicechange", onDeviceChange);
    apply(false);
    return () => {
      cancelled = true;
      disarm();
      navigator.mediaDevices?.removeEventListener?.("devicechange", onDeviceChange);
    };
  }, [activeAudioSinkId, track]);

  return null;
};
